import type { EChartsOption } from 'echarts';
import { useMemo } from 'react';

import { seriesColor, useTheme } from '../../theme/readTheme';
import type { ChartData, ChartFields } from '../../types';
import { EChart } from '../EChart';
import { Plot } from '../Plot';
import { Empty } from '../states';
import { cell, humanise, isNumeric } from '../format';
import { animation, axisTooltip, baseGrid, valueAxisX, valueAxisY } from '../options';

/**
 * The frequency distribution of one numeric column.
 *
 * A histogram is not a bar chart: its x axis is a continuous measure cut into
 * equal-width bins, and the bars touch because the ranges are contiguous. It
 * answers "how are these values spread?" — where the mass sits, whether it is
 * skewed, whether there are two humps — which a bar chart of raw rows cannot.
 *
 * The first numeric column is binned. Bin count follows the Freedman–Diaconis
 * spirit but is clamped to a readable 5–30 so a tile never fills with hairlines
 * or collapses to three blocks.
 */

interface Props {
  data: ChartData;
  /** Author's column choices; absent means infer. */
  fields?: ChartFields;
}

const MIN_BINS = 5;
const MAX_BINS = 30;

const histogramView = (data: ChartData, theme: ReturnType<typeof useTheme>) => {
  const numericIndex = data.columns.findIndex((_, i) =>
    data.rows.slice(0, 30).some((r) => isNumeric(r[i])),
  );
  if (numericIndex < 0) return null;

  const values = data.rows.map((r) => r[numericIndex]).filter(isNumeric);
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return null;

  // Bin count from the sample size, clamped so the tile stays legible.
  const bins = Math.max(MIN_BINS, Math.min(MAX_BINS, Math.ceil(Math.sqrt(values.length))));
  const width = (max - min) / bins;

  const counts = new Array<number>(bins).fill(0);
  for (const v of values) {
    // The max value lands in the last bin, not a phantom bin+1.
    const idx = Math.min(bins - 1, Math.floor((v - min) / width));
    counts[idx] += 1;
  }

  const labels = counts.map((_, i) => {
    const lo = min + i * width;
    const hi = lo + width;
    return `${cell(lo)}–${cell(hi)}`;
  });

  const measureName = humanise(data.columns[numericIndex]);
  const frameLike = {
    categories: labels,
    categoryName: measureName,
    series: [{ name: 'Count', values: counts }],
    measureName: 'Count',
    temporal: false,
    rowCount: counts.length,
    categoryCount: counts.length,
    truncated: false,
    omitted: [],
  };

  const option: EChartsOption = {
    ...animation,
    grid: baseGrid({ legend: false, rotatedLabels: bins > 10 }),
    xAxis: {
      type: 'category' as const,
      data: labels,
      name: measureName,
      nameLocation: 'middle' as const,
      nameGap: bins > 10 ? 34 : 22,
      nameTextStyle: { color: theme.inkMuted, fontSize: 11, fontFamily: theme.font },
      axisLine: { lineStyle: { color: theme.axis, width: 1 } },
      axisTick: { show: false },
      axisLabel: {
        color: theme.inkMuted,
        fontSize: 11,
        fontFamily: theme.font,
        hideOverlap: true,
        rotate: bins > 10 ? 35 : 0,
      },
    },
    yAxis: valueAxisY(theme, { name: 'Count' }),
    tooltip: axisTooltip(theme, frameLike),
    series: [
      {
        type: 'bar' as const,
        data: counts,
        // Contiguous ranges touch — no category gap, a hairline surface seam
        // between bars instead.
        barCategoryGap: '0%',
        itemStyle: {
          color: seriesColor(theme, 0),
          borderColor: theme.surface,
          borderWidth: 1,
          borderRadius: [3, 3, 0, 0],
        },
      },
    ],
  };

  return { option };
};

export const Histogram = ({ data }: Props) => {
  const theme = useTheme();
  const view = useMemo(() => histogramView(data, theme), [data, theme]);
  if (!view) return <Empty message="Needs a numeric column to bin" />;
  return (
    <Plot>
      <EChart option={view.option} ariaLabel="Histogram" />
    </Plot>
  );
};
